import { usePathname, useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';

import useProductAnalytics from '@/analytics/hooks/useProductAnalytics';
import { OR4_PDP_SESSION_STORAGE_KEY } from '@/app/[locale]/product/_consts/sessionStorage';
import MembershipSelection from '@/app/[locale]/product/_steps/enums/MembershipSelection';
import { useCartContext } from '@/app/contexts/CartProvider';
import logToDatadog from '@/logger/logToDatadog';
import { addToCart } from '@/queries/AddToCart';
import { changeWarranty } from '@/queries/ChangeWarranty';
import type { ListProduct } from '@/queries/Products';
import { removeFromCart } from '@/queries/RemoveFromCart';
import { removeFromSessionStorage } from '@/utils/sessionStorage';

import type { Order } from '../_types/order';

export interface AddOrderToCartProps {
  order: Order;
  product: ListProduct | undefined;
  setIsAddingToCart: (isAdding: boolean) => void;
}

const useAddOrderToCart = ({
  order,
  product,
  setIsAddingToCart,
}: AddOrderToCartProps) => {
  const { refreshCart } = useCartContext();
  const cart = refreshCart?.cart;
  const ringCount = cart?.ringCount ?? 0;
  const { sendProductAddedEvent } = useProductAnalytics();
  const locale = useLocale();
  const path = usePathname();
  const router = useRouter();

  const membershipSelection = order.membership.value as MembershipSelection;
  const membershipSku = order.membership.sku;
  const sizeSku = order.size.sku;
  const warrantySku = order.warranty.sku;
  const stockStatus = order.size.stockStatus;

  const handleAddToCart = async () => {
    const onSuccess = async () => {
      removeFromSessionStorage(OR4_PDP_SESSION_STORAGE_KEY);
      await sendProductAddedEvent({
        path,
        product,
        stockStatus,
      });

      router.push(`/${locale}/cart`);
    };
    // disable the add to cart button while the request is in progress
    setIsAddingToCart(true);

    try {
      await addToCart(sizeSku, warrantySku);
      if (membershipSelection === MembershipSelection.OneYear) {
        await addToCart(membershipSku, warrantySku);
      } else {
        await removeFromCart(membershipSku, warrantySku);
      }
      if (ringCount > 0) {
        try {
          await changeWarranty(warrantySku);
          await onSuccess();
        } catch (e) {
          void logToDatadog(
            'cart',
            'cart 2.0 changeWarranty failed after addToCart',
            { cause: e, warrantySku, ringCount },
          );
        }
      } else {
        await onSuccess();
      }
    } catch (e) {
      void logToDatadog(
        'cart',
        'Error thrown during addToCart (cart 2.0)',
        { error: e },
        'error',
      );
    }
  };
  return { onAddToCart: handleAddToCart };
};

export default useAddOrderToCart;
